/**
 * Error logging service for reporting frontend errors to the backend
 */

import StackTrace from "stacktrace-js";
import { getRuntimeFlags, getStartupMarks } from "@/lib/runtimeDiagnostics";

export enum ErrorSeverity {
  CRITICAL = "critical",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

export interface FrontendErrorReport {
  error_type?: string;
  error_message: string;
  stack_trace?: string;
  url: string; // Full URL for reference only
  pathname?: string; // Clean pathname (e.g., "/form")
  route?: string; // Next.js route (e.g., "/(public)/form")
  component?: string;
  user_id?: number;
  user_email?: string;
  session_id?: string;
  user_agent?: string;
  browser_info?: {
    browser?: string;
    version?: string;
    os?: string;
    viewport?: {
      width: number;
      height: number;
    };
  };
  severity?: ErrorSeverity;
  extra_data?: Record<string, unknown>;
  // Enhanced error location tracking from source code
  source_file?: string; // Source file path (e.g., "components/TarotForm.tsx")
  source_line?: number; // Line number in source file
  source_column?: number; // Column number in source file
  source_function?: string; // Function/method name
  // Component hierarchy for React errors
  component_stack?: string;
  // Original minified location (for comparison)
  minified_file?: string;
  minified_line?: number;
  minified_column?: number;
}

export class ErrorLogService {
  private static readonly PROXY_BASE = "/api/proxy";
  private static readonly ERROR_LOGS_ENDPOINT = "/api/error-logs/frontend";

  /**
   * Get browser information
   */
  private static getBrowserInfo() {
    if (typeof window === "undefined") return undefined;

    const userAgent = navigator.userAgent;
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Simple browser detection
    let browser = "Unknown";
    if (userAgent.indexOf("Firefox") > -1) {
      browser = "Firefox";
    } else if (userAgent.indexOf("Chrome") > -1) {
      browser = "Chrome";
    } else if (userAgent.indexOf("Safari") > -1) {
      browser = "Safari";
    } else if (userAgent.indexOf("Edge") > -1) {
      browser = "Edge";
    }

    // Simple OS detection
    let os = "Unknown";
    if (userAgent.indexOf("Win") > -1) {
      os = "Windows";
    } else if (userAgent.indexOf("Mac") > -1) {
      os = "MacOS";
    } else if (userAgent.indexOf("Linux") > -1) {
      os = "Linux";
    } else if (userAgent.indexOf("Android") > -1) {
      os = "Android";
    } else if (userAgent.indexOf("iOS") > -1) {
      os = "iOS";
    }

    return {
      browser,
      os,
      viewport,
      version: userAgent,
    };
  }

  /**
   * Get session ID from localStorage or generate a new one
   */
  private static getSessionId(): string {
    if (typeof window === "undefined") return "ssr-session";

    const STORAGE_KEY = "error_log_session_id";
    let sessionId = localStorage.getItem(STORAGE_KEY);

    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      localStorage.setItem(STORAGE_KEY, sessionId);
    }

    return sessionId;
  }

  /**
   * Parse stack trace to extract detailed location information
   * Captures both minified location and attempts to identify source location
   */
  private static parseStackTrace(stack?: string): {
    sourceFile?: string;
    sourceLine?: number;
    sourceColumn?: number;
    sourceFunction?: string;
    minifiedFile?: string;
    minifiedLine?: number;
    minifiedColumn?: number;
    fullStackFrames?: string[];
  } {
    if (!stack) return {};

    try {
      const lines = stack.split("\n");
      const stackFrames: string[] = [];
      let firstFrame: {
        functionName: string;
        filePath: string;
        lineNumber: number;
        columnNumber: number;
      } | null = null;

      // Parse all stack frames for comprehensive logging
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        stackFrames.push(line);

        // Skip if we already found the first meaningful frame
        if (firstFrame) continue;

        // Chrome/Edge format: "at FunctionName (http://domain/path/file.js:line:column)"
        const chromeMatch = line.match(
          /at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/
        );
        if (chromeMatch) {
          const [, funcName, filePath, lineNum, colNum] = chromeMatch;

          // Skip browser internals and node_modules in stack
          if (
            filePath.includes("node_modules") ||
            filePath.includes("webpack-internal") ||
            filePath.includes("webpack/runtime")
          ) {
            continue;
          }

          firstFrame = {
            functionName: funcName?.trim() || "anonymous",
            filePath: filePath,
            lineNumber: parseInt(lineNum, 10),
            columnNumber: parseInt(colNum, 10),
          };
        } else {
          // Firefox format: "FunctionName@http://domain/path/file.js:line:column"
          const firefoxMatch = line.match(/(.+?)@(.+?):(\d+):(\d+)$/);
          if (firefoxMatch) {
            const [, funcName, filePath, lineNum, colNum] = firefoxMatch;

            if (
              filePath.includes("node_modules") ||
              filePath.includes("webpack-internal") ||
              filePath.includes("webpack/runtime")
            ) {
              continue;
            }

            firstFrame = {
              functionName: funcName?.trim() || "anonymous",
              filePath: filePath,
              lineNumber: parseInt(lineNum, 10),
              columnNumber: parseInt(colNum, 10),
            };
          }
        }
      }

      if (!firstFrame) {
        return { fullStackFrames: stackFrames };
      }

      // Extract meaningful file information
      const extractedFile = this.extractSourceFileInfo(firstFrame.filePath);

      return {
        // Source information (best guess from URL structure)
        sourceFile: extractedFile.sourceFile,
        sourceLine: firstFrame.lineNumber,
        sourceColumn: firstFrame.columnNumber,
        sourceFunction: firstFrame.functionName,
        // Minified location (actual bundle location)
        minifiedFile: extractedFile.minifiedFile,
        minifiedLine: firstFrame.lineNumber,
        minifiedColumn: firstFrame.columnNumber,
        // Full stack for detailed analysis
        fullStackFrames: stackFrames.slice(0, 10), // Limit to first 10 frames
      };
    } catch (error) {
      console.error("Failed to parse stack trace:", error);
    }

    return {};
  }

  /**
   * Normalize file names from source maps / webpack URLs
   */
  private static normalizeSourceFileName(fileName: string): string {
    try {
      let cleaned = fileName.split("?")[0].split("#")[0];

      // Handle webpack:// URLs
      if (cleaned.startsWith("webpack://")) {
        cleaned = cleaned.replace("webpack://", "");
        cleaned = cleaned.replace(/^_N_E\//, "");
        cleaned = cleaned.replace(/^\.\//, "");
      }

      // Handle file:// URLs
      if (cleaned.startsWith("file://")) {
        cleaned = cleaned.replace("file://", "");
      }

      // Handle http(s) URLs
      if (cleaned.startsWith("http://") || cleaned.startsWith("https://")) {
        const url = new URL(cleaned);
        cleaned = url.pathname.replace(/^\//, "");
      }

      return cleaned;
    } catch {
      return fileName;
    }
  }

  /**
   * Try to resolve source-mapped stack frames (real file/line) using stacktrace-js
   */
  private static async getSourceMappedStack(error: Error): Promise<{
    sourceFile?: string;
    sourceLine?: number;
    sourceColumn?: number;
    sourceFunction?: string;
    fullStackFrames?: string[];
  }> {
    try {
      const frames = await StackTrace.fromError(error);
      const stackFrames = frames.map((frame) => {
        const file = frame.fileName
          ? this.normalizeSourceFileName(frame.fileName)
          : "unknown";
        const line = frame.lineNumber ?? 0;
        const column = frame.columnNumber ?? 0;
        const fn = frame.functionName || "anonymous";
        return `at ${fn} (${file}:${line}:${column})`;
      });

      const firstFrame = frames.find((frame) => {
        const fileName = frame.fileName || "";
        return (
          fileName &&
          !fileName.includes("node_modules") &&
          !fileName.includes("webpack-internal") &&
          !fileName.includes("webpack/runtime")
        );
      });

      if (!firstFrame) {
        return { fullStackFrames: stackFrames.slice(0, 10) };
      }

      return {
        sourceFile: firstFrame.fileName
          ? this.normalizeSourceFileName(firstFrame.fileName)
          : undefined,
        sourceLine: firstFrame.lineNumber ?? undefined,
        sourceColumn: firstFrame.columnNumber ?? undefined,
        sourceFunction: firstFrame.functionName || undefined,
        fullStackFrames: stackFrames.slice(0, 10),
      };
    } catch (error) {
      console.error("Failed to resolve source-mapped stack:", error);
      return {};
    }
  }

  /**
   * Extract source file information from bundle path
   * Attempts to identify the actual source file from Next.js chunk names
   */
  private static extractSourceFileInfo(filePath: string): {
    sourceFile?: string;
    minifiedFile: string;
  } {
    try {
      // Remove query parameters and hash
      const cleanPath = filePath.split("?")[0].split("#")[0];

      // Extract file name from URL
      const urlParts = cleanPath.split("/");
      const fileName = urlParts[urlParts.length - 1];

      // Next.js chunk naming patterns to identify source files
      // Examples:
      // - "app-pages-browser_src_components_TarotForm_tsx.js" -> "components/TarotForm.tsx"
      // - "page-1234.js" -> bundle chunk (no clear source)
      // - "_app-abc123.js" -> app file

      let sourceFile: string | undefined;

      // Try to extract from Next.js chunk name
      if (fileName.includes("_src_")) {
        // Pattern: ..._src_folder_file_ext.js
        const match = fileName.match(/_src_(.+?)\.(js|ts|tsx|jsx)/);
        if (match) {
          // Convert underscore path to slash path
          sourceFile = match[1].replace(/_/g, "/") + "." + match[2];
        }
      } else if (
        fileName.includes("components") ||
        fileName.includes("pages") ||
        fileName.includes("app")
      ) {
        // Try to extract component/page name
        const match = fileName.match(/([a-zA-Z0-9_-]+)\.(js|ts|tsx|jsx)/);
        if (match) {
          sourceFile = match[1];
        }
      }

      // Get meaningful minified file reference
      let minifiedFile = fileName;
      if (urlParts.length >= 2) {
        // Include parent folder for context (e.g., "_next/static/chunks/file.js")
        minifiedFile = `${urlParts[urlParts.length - 2]}/${fileName}`;
      }

      return {
        sourceFile,
        minifiedFile,
      };
    } catch {
      return { minifiedFile: filePath };
    }
  }

  /**
   * Extract clean pathname and route from current URL
   */
  private static extractPageInfo(): {
    pathname: string;
    route: string;
    queryParams?: Record<string, string>;
  } {
    if (typeof window === "undefined") {
      return { pathname: "/ssr", route: "/ssr" };
    }

    const url = new URL(window.location.href);
    const pathname = url.pathname;

    // Extract query parameters (without UTM spam)
    const queryParams: Record<string, string> = {};
    const relevantParams = ["id", "step", "section", "page", "tab"]; // Add relevant params here

    url.searchParams.forEach((value, key) => {
      // Only include non-UTM and relevant params
      if (
        !key.startsWith("utm_") &&
        !key.startsWith("fb") &&
        !key.startsWith("_ga")
      ) {
        if (relevantParams.includes(key) || relevantParams.length === 0) {
          queryParams[key] = value;
        }
      }
    });

    // Determine Next.js route (pathname is usually good enough)
    // For dynamic routes, this would show the actual route with params
    const route = pathname;

    return {
      pathname,
      route,
      queryParams:
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
    };
  }

  /**
   * Log an error to the backend
   */
  static async logError(
    error: Error | string,
    context?: {
      component?: string;
      severity?: ErrorSeverity;
      extra_data?: Record<string, unknown>;
      user_id?: number;
      user_email?: string;
      componentStack?: string; // For React error boundaries
    }
  ): Promise<void> {
    try {
      // Skip logging in development if desired (optional)
      // if (process.env.NODE_ENV === 'development') return;

      const errorMessage = typeof error === "string" ? error : error.message;
      const errorType = typeof error === "string" ? undefined : error.name;
      const stackTrace = typeof error === "string" ? undefined : error.stack;

      // ChunkLoadError repete a cada re-render do error boundary enquanto o
      // usuário está preso na tela de erro — uma sessão chegou a gerar 6
      // registros idênticos. Um por mensagem por página já conta a ocorrência.
      if (errorMessage?.startsWith("Failed to load chunk")) {
        if (ErrorLogService.chunkErrorsLogged.has(errorMessage)) return;
        ErrorLogService.chunkErrorsLogged.add(errorMessage);
      }

      // Parse stack trace for minified location info
      const stackInfo = this.parseStackTrace(stackTrace);

      // Try to resolve source-mapped stack frames (real source file/line)
      const sourceMappedInfo =
        typeof error === "string" ? {} : await this.getSourceMappedStack(error);

      // Extract clean page information
      const pageInfo = this.extractPageInfo();
      const runtimeFlags = getRuntimeFlags();
      const startupMarks = getStartupMarks().slice(-20);

      const errorReport: FrontendErrorReport = {
        error_type: errorType,
        error_message: errorMessage,
        stack_trace: stackTrace,
        url: typeof window !== "undefined" ? window.location.href : "ssr",
        pathname: pageInfo.pathname,
        route: pageInfo.route,
        component: context?.component,
        severity: context?.severity || ErrorSeverity.ERROR,
        user_id: context?.user_id,
        user_email: context?.user_email,
        session_id: this.getSessionId(),
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        browser_info: this.getBrowserInfo(),
        extra_data: {
          ...(context?.extra_data || {}),
          query_params: pageInfo.queryParams,
          stack_frames:
            sourceMappedInfo.fullStackFrames || stackInfo.fullStackFrames,
          runtime_flags: runtimeFlags,
          startup_marks: startupMarks,
          document_ready_state:
            typeof document !== "undefined" ? document.readyState : undefined,
        },
        // Enhanced source location tracking (prefer source-mapped)
        source_file: sourceMappedInfo.sourceFile || stackInfo.sourceFile,
        source_line: sourceMappedInfo.sourceLine || stackInfo.sourceLine,
        source_column: sourceMappedInfo.sourceColumn || stackInfo.sourceColumn,
        source_function:
          sourceMappedInfo.sourceFunction || stackInfo.sourceFunction,
        // React component stack
        component_stack: context?.componentStack,
        // Minified location for reference
        minified_file: stackInfo.minifiedFile,
        minified_line: stackInfo.minifiedLine,
        minified_column: stackInfo.minifiedColumn,
      };

      // Send error report to backend
      const response = await fetch(
        `${this.PROXY_BASE}${this.ERROR_LOGS_ENDPOINT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(errorReport),
          credentials: "include",
        }
      );

      if (!response.ok) {
        // If logging fails, log to console
        console.error("Failed to log error to backend:", response.statusText);
      }
    } catch (logError) {
      // If the error logging itself fails, just log to console
      console.error("Error logging service failed:", logError);
    }
  }

  /**
   * Log informational message (not an error) - LOCAL ONLY, not sent to server
   * Use this for tracking events, state changes, or debugging info
   * These logs only appear in browser console to avoid server overload
   */
  static async logInfo(
    message: string,
    context?: {
      component?: string;
      extra_data?: Record<string, unknown>;
      user_id?: number;
      user_email?: string;
    }
  ): Promise<void> {
    // Only log to console, do NOT send to server (prevents log accumulation)
    if (typeof console !== "undefined") {
      const logData = {
        message,
        component: context?.component,
        extra_data: context?.extra_data,
        timestamp: new Date().toISOString(),
      };

      // Use console.info with styled output for easy identification
      console.info(
        `%c[${context?.component || "App"}] ${message}`,
        "color: #0066cc; font-weight: bold",
        logData.extra_data
      );
    }
  }

  // Quantas vezes cada endpoint+status já foi reportado nesta página. Chamadas
  // em loop (ex.: polling de status de pagamento) repetem o mesmo erro dezenas
  // de vezes; sem o limite, cada repetição virava um registro no backend.
  private static apiErrorCounts = new Map<string, number>();
  private static readonly MAX_API_ERROR_REPEATS = 3;

  // Mensagens de ChunkLoadError já registradas nesta página (ver logError).
  private static chunkErrorsLogged = new Set<string>();

  /**
   * Log an API error (failed API call)
   */
  static async logApiError(
    endpoint: string,
    error: Error | string,
    statusCode?: number,
    responseData?: unknown
  ): Promise<void> {
    const dedupeKey = `${endpoint}|${statusCode ?? "?"}`;
    const seen = (this.apiErrorCounts.get(dedupeKey) ?? 0) + 1;
    this.apiErrorCounts.set(dedupeKey, seen);
    if (seen > this.MAX_API_ERROR_REPEATS) return;

    await this.logError(error, {
      component: "API",
      severity:
        statusCode && statusCode >= 500
          ? ErrorSeverity.ERROR
          : ErrorSeverity.WARNING,
      extra_data: {
        endpoint,
        status_code: statusCode,
        response: responseData,
        repeat_count: seen,
        ...(seen === this.MAX_API_ERROR_REPEATS
          ? { further_repeats_suppressed: true }
          : {}),
      },
    });
  }

  /**
   * Log a React component error
   */
  static async logComponentError(
    componentName: string,
    error: Error,
    errorInfo?: { componentStack?: string }
  ): Promise<void> {
    await this.logError(error, {
      component: componentName,
      severity: ErrorSeverity.ERROR,
      componentStack: errorInfo?.componentStack, // Pass component stack directly
      extra_data: {
        error_boundary: true,
      },
    });
  }
}

const CHUNK_RELOAD_GUARD_KEY = "chunk_reload_guard_v1";
const OPAQUE_SCRIPT_ERROR_GUARD_KEY = "opaque_script_error_guard_v1";
const OPAQUE_SCRIPT_ERROR_DEDUPE_WINDOW_MS = 60 * 1000;
const MAX_IDENTICAL_GLOBAL_ERROR_REPORTS = 3;
const MAX_GLOBAL_ERROR_REPORTS_PER_PAGE = 100;

type GlobalErrorHandlerRegistration = {
  errorHandler: (event: ErrorEvent) => void;
  rejectionHandler: (event: PromiseRejectionEvent) => void;
  consumers: number;
};

let globalErrorHandlerRegistration: GlobalErrorHandlerRegistration | null =
  null;

function opaqueScriptErrorReportDecision(): {
  shouldReport: boolean;
  previousSuppressedCount: number;
} {
  const now = Date.now();

  try {
    const raw = sessionStorage.getItem(OPAQUE_SCRIPT_ERROR_GUARD_KEY);
    const guard = raw
      ? (JSON.parse(raw) as { at?: number; suppressedCount?: number })
      : null;
    const isRecent =
      typeof guard?.at === "number" &&
      now - guard.at < OPAQUE_SCRIPT_ERROR_DEDUPE_WINDOW_MS;

    if (isRecent) {
      sessionStorage.setItem(
        OPAQUE_SCRIPT_ERROR_GUARD_KEY,
        JSON.stringify({
          at: guard.at,
          suppressedCount: (guard.suppressedCount || 0) + 1,
        }),
      );
      return { shouldReport: false, previousSuppressedCount: 0 };
    }

    const previousSuppressedCount = guard?.suppressedCount || 0;
    sessionStorage.setItem(
      OPAQUE_SCRIPT_ERROR_GUARD_KEY,
      JSON.stringify({ at: now, suppressedCount: 0 }),
    );
    return { shouldReport: true, previousSuppressedCount };
  } catch {
    // Se o storage estiver bloqueado, preserva o diagnóstico em vez de ocultar
    // completamente um evento que já não contém origem ou stack.
    return { shouldReport: true, previousSuppressedCount: 0 };
  }
}

function createGlobalErrorHandlerCleanup(
  registration: GlobalErrorHandlerRegistration,
): () => void {
  let released = false;

  return () => {
    if (released) return;
    released = true;
    registration.consumers = Math.max(0, registration.consumers - 1);
    if (registration.consumers > 0 || typeof window === "undefined") return;

    window.removeEventListener("error", registration.errorHandler);
    window.removeEventListener(
      "unhandledrejection",
      registration.rejectionHandler,
    );
    if (globalErrorHandlerRegistration === registration) {
      globalErrorHandlerRegistration = null;
    }
  };
}

function isChunkLoadErrorLike(payload: {
  name?: string;
  message?: string;
  stack?: string;
  filename?: string;
}): boolean {
  const text = `${payload.message || ""}\n${payload.stack || ""}\n${
    payload.filename || ""
  }`.toLowerCase();
  const name = (payload.name || "").toLowerCase();
  const isGenericLoadFailed =
    name === "typeerror" && (payload.message || "").trim().toLowerCase() === "load failed";

  return (
    text.includes("chunkloaderror") ||
    text.includes("loading chunk") ||
    text.includes("failed to load chunk") ||
    text.includes("failed to fetch dynamically imported module") ||
    text.includes("importing a module script failed") ||
    text.includes("load failed for the module with source") ||
    text.includes("/_next/static/chunks/") ||
    isGenericLoadFailed
  );
}

function isBrowserExtensionErrorLike(payload: {
  message?: string;
  stack?: string;
  filename?: string;
}): boolean {
  const text = `${payload.message || ""}\n${payload.stack || ""}\n${
    payload.filename || ""
  }`.toLowerCase();

  return (
    text.includes("chrome-extension://") ||
    text.includes("moz-extension://") ||
    text.includes("safari-web-extension://") ||
    text.includes("ms-browser-extension://")
  );
}

function isWebkitBridgeErrorLike(message?: string): boolean {
  const text = (message || "").toLowerCase();
  return (
    text.includes("window.webkit.messagehandlers") ||
    text.includes("webkit.messagehandlers") ||
    (text.includes("webkit") && text.includes("postmessage"))
  );
}

function isFacebookInAppUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("[fb_iab") || ua.includes("fban") || ua.includes("fbav");
}

function isInAppAutofillBridgeValueError(payload: {
  message?: string;
  stack?: string;
}): boolean {
  const text = `${payload.message || ""}\n${payload.stack || ""}`.toLowerCase();
  return (
    text.includes("cannot read properties of undefined (reading 'value')") &&
    text.includes("setcontactautofillvaluesfrombridge")
  );
}

function isInAppJavaBridgeGoneError(message?: string): boolean {
  const text = (message || "").toLowerCase();
  return text.includes("error invoking postmessage: java object is gone");
}

function tryRecoverChunkLoadError(source: "error" | "unhandledrejection"): {
  recovered: boolean;
  reason?: string;
} {
  if (typeof window === "undefined") return { recovered: false };

  try {
    const guardRaw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    const currentPath = window.location.pathname;
    const guard = guardRaw ? JSON.parse(guardRaw) : null;

    // Only one automatic recovery per route in the same tab session.
    if (guard && guard.path === currentPath) {
      return { recovered: false, reason: "already_retried_for_route" };
    }

    sessionStorage.setItem(
      CHUNK_RELOAD_GUARD_KEY,
      JSON.stringify({
        path: currentPath,
        at: Date.now(),
        source,
      }),
    );

    const url = new URL(window.location.href);
    url.searchParams.set("__chunk_retry", String(Date.now()));
    window.location.assign(url.toString());

    return { recovered: true };
  } catch {
    return { recovered: false, reason: "guard_storage_failed" };
  }
}

/**
 * Global error handler for unhandled errors
 */
export function setupGlobalErrorHandler(): () => void {
  if (typeof window === "undefined") return () => {};

  if (globalErrorHandlerRegistration) {
    globalErrorHandlerRegistration.consumers += 1;
    return createGlobalErrorHandlerCleanup(globalErrorHandlerRegistration);
  }

  // Cleanup old guard from previous sessions/deploys.
  try {
    const guardRaw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    if (guardRaw) {
      const guard = JSON.parse(guardRaw) as { at?: number };
      const isStale =
        typeof guard?.at !== "number" || Date.now() - guard.at > 10 * 60 * 1000;
      if (isStale) {
        sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
      }
    }
  } catch {}

  const globalErrorReportCounts = new Map<string, number>();
  let totalGlobalErrorReports = 0;

  const reportGlobalError = (
    error: Error | string,
    context?: Parameters<typeof ErrorLogService.logError>[1],
  ) => {
    if (totalGlobalErrorReports >= MAX_GLOBAL_ERROR_REPORTS_PER_PAGE) return;

    const message = typeof error === "string" ? error : error.message;
    const stack = typeof error === "string" ? "" : error.stack || "";
    const diagnosticType = String(context?.extra_data?.type || "");
    const fingerprint = `${diagnosticType}\n${message}\n${stack}`;
    const seen = (globalErrorReportCounts.get(fingerprint) || 0) + 1;
    globalErrorReportCounts.set(fingerprint, seen);
    if (seen > MAX_IDENTICAL_GLOBAL_ERROR_REPORTS) return;

    totalGlobalErrorReports += 1;
    void ErrorLogService.logError(error, context);
  };

  // Handle uncaught errors (register all)
  const handleError = (event: ErrorEvent) => {
    const errorMessage = event.error?.message || event.message || "";
    const errorStack = event.error?.stack;
    const isFacebookIab = isFacebookInAppUserAgent();
    const isBridgeError = isWebkitBridgeErrorLike(errorMessage);
    const isOpaqueScriptError =
      (errorMessage || "").trim().toLowerCase() === "script error." &&
      !event.filename &&
      event.lineno === 0 &&
      event.colno === 0;
    const isJavaBridgeGoneInApp =
      isInAppJavaBridgeGoneError(errorMessage) &&
      (isFacebookIab || getRuntimeFlags()?.inAppSafeMode === true);
    const isAutofillBridgeError =
      isInAppAutofillBridgeValueError({
        message: errorMessage,
        stack: errorStack,
      }) && isFacebookIab;
    const runtimeFlags = getRuntimeFlags();
    const isBrowserExtensionError = isBrowserExtensionErrorLike({
      message: errorMessage,
      stack: errorStack,
      filename: event.filename,
    });
    const isChunkError = isChunkLoadErrorLike({
      name: event.error?.name,
      message: errorMessage,
      stack: event.error?.stack,
      filename: event.filename,
    });

    // Extensões executam scripts dentro da página, mas seus erros não são
    // acionáveis pela aplicação. Ignorá-los evita tempestades de logs
    // como a causada pelo executor da extensão Urban VPN.
    if (isBrowserExtensionError) return;

    if (isBridgeError) {
      try {
        event.preventDefault();
      } catch {}
      reportGlobalError(event.error || event.message || "In-app bridge error", {
        severity: ErrorSeverity.WARNING,
        extra_data: {
          type: "in_app_bridge_error",
          source_event: "error",
          handled_silently: true,
          minified_filename: event.filename,
          minified_lineno: event.lineno,
          minified_colno: event.colno,
        },
      });
      return;
    }

    if (isOpaqueScriptError) {
      try {
        event.preventDefault();
      } catch {}
      const decision = opaqueScriptErrorReportDecision();
      if (decision.shouldReport) {
        reportGlobalError(
          event.error || event.message || "Opaque script error",
          {
            severity: ErrorSeverity.WARNING,
            extra_data: {
              type: "opaque_script_error",
              handled_silently: true,
              dedupe_window_ms: OPAQUE_SCRIPT_ERROR_DEDUPE_WINDOW_MS,
              previous_suppressed_count: decision.previousSuppressedCount,
              detected_in_app:
                runtimeFlags?.inAppSafeMode === true || isFacebookIab,
              minified_filename: event.filename,
              minified_lineno: event.lineno,
              minified_colno: event.colno,
              opaque_script_error: true,
            },
          },
        );
      }
      return;
    }

    if (isAutofillBridgeError) {
      try {
        event.preventDefault();
      } catch {}
      reportGlobalError(
        event.error || event.message || "In-app autofill bridge error",
        {
          severity: ErrorSeverity.WARNING,
          extra_data: {
            type: "in_app_autofill_bridge_error",
            handled_silently: true,
            minified_filename: event.filename,
            minified_lineno: event.lineno,
            minified_colno: event.colno,
          },
        },
      );
      return;
    }

    if (isJavaBridgeGoneInApp) {
      try {
        event.preventDefault();
      } catch {}
      reportGlobalError(
        event.error || event.message || "In-app Java bridge unavailable",
        {
          severity: ErrorSeverity.WARNING,
          extra_data: {
            type: "in_app_java_bridge_gone_error",
            handled_silently: true,
            minified_filename: event.filename,
            minified_lineno: event.lineno,
            minified_colno: event.colno,
          },
        },
      );
      return;
    }

    if (isChunkError) {
      const recovery = tryRecoverChunkLoadError("error");
      reportGlobalError(event.error || event.message || "Chunk load error", {
        severity: ErrorSeverity.WARNING,
        extra_data: {
          type: "chunk_load_error",
          auto_recovery_attempted: true,
          auto_recovery_triggered: recovery.recovered,
          auto_recovery_reason: recovery.reason,
          minified_filename: event.filename,
          minified_lineno: event.lineno,
          minified_colno: event.colno,
        },
      });

      if (recovery.recovered) return;
    }

    reportGlobalError(event.error || event.message || "Unknown window error", {
      severity: ErrorSeverity.ERROR,
      extra_data: {
        type: "window_error",
        opaque_script_error: isOpaqueScriptError,
        minified_filename: event.filename,
        minified_lineno: event.lineno,
        minified_colno: event.colno,
      },
    });
  };

  // Handle unhandled promise rejections (register all)
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const rejectionMessage =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason || "");
    const rejectionStack =
      event.reason instanceof Error ? event.reason.stack : undefined;
    const isBridgeError = isWebkitBridgeErrorLike(rejectionMessage);
    const isBrowserExtensionError = isBrowserExtensionErrorLike({
      message: rejectionMessage,
      stack: rejectionStack,
    });

    const isChunkError = isChunkLoadErrorLike({
      name: event.reason instanceof Error ? event.reason.name : undefined,
      message: rejectionMessage,
      stack: rejectionStack,
    });

    if (isBrowserExtensionError) return;

    if (isBridgeError) {
      try {
        event.preventDefault();
      } catch {}
      reportGlobalError(
        event.reason instanceof Error ? event.reason : String(event.reason),
        {
          severity: ErrorSeverity.WARNING,
          extra_data: {
            type: "in_app_bridge_error",
            source_event: "unhandledrejection",
            handled_silently: true,
          },
        }
      );
      return;
    }

    if (isChunkError) {
      const recovery = tryRecoverChunkLoadError("unhandledrejection");
      reportGlobalError(
        event.reason instanceof Error ? event.reason : String(event.reason),
        {
          severity: ErrorSeverity.WARNING,
          extra_data: {
            type: "chunk_load_error",
            auto_recovery_attempted: true,
            auto_recovery_triggered: recovery.recovered,
            auto_recovery_reason: recovery.reason,
          },
        }
      );

      if (recovery.recovered) return;
    }

    reportGlobalError(
      event.reason instanceof Error ? event.reason : String(event.reason),
      {
        severity: ErrorSeverity.WARNING,
        extra_data: {
          type: "unhandled_promise_rejection",
        },
      }
    );
  };

  const registration: GlobalErrorHandlerRegistration = {
    errorHandler: handleError,
    rejectionHandler: handleUnhandledRejection,
    consumers: 1,
  };
  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  globalErrorHandlerRegistration = registration;

  return createGlobalErrorHandlerCleanup(registration);
}
